import { Link, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { Home, Activity, Sparkles } from "lucide-react"; 

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation(); // to highlight active route

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 40);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    ["Home", "#home", Home],
    ["About", "#about", "/images/icons/temple.png"], 
    ["Prabhupada", "#prabhupada", "/images/icons/prabhupada.png"], 
    ["Activities", "#activities", Activity],
    ["Seva", "#seva", Sparkles],
  ];

  const scrollToSection = (hash) => {
  const el = document.querySelector(hash);

  if (location.pathname !== "/") {
    // Navigate to home first
    window.location.href = "/" + hash;
    return;
  }

  if (el) {
    el.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }
};


  return (
    <>
      {/* MAIN NAVBAR */}
      <nav
        className={`fixed z-50 transition-all duration-500 ease-out
        ${
          scrolled
            ? "top-3 left-4 right-4 rounded-2xl bg-white/85 backdrop-blur-2xl shadow-[0_20px_60px_rgba(0,0,0,0.12)] border border-white/40"
            : "top-0 left-0 right-0 bg-black/35 backdrop-blur-xl"
        }`}
      >
        <div className="flex justify-between items-center max-w-7xl mx-auto px-6 py-2.5">

          {/* LOGO */}
          <Link to="/" className="flex items-center">
            <img
              src={
                scrolled
                  ? "/images/logos/iskcon-jaipur-black.png"
                  : "/images/logos/iskcon-jaipur-white.png"
              }
              alt="ISKCON Jaipur"
              className="h-11 md:h-12 object-contain transition-all duration-300"
            />
          </Link>

          {/* DESKTOP MENU */}
          <div className="hidden md:flex items-center space-x-8 text-sm">
            {navLinks.map(([label, path]) => (
              <Link
                key={label}
                onClick={() => scrollToSection(path)}
                className={`
                  relative
                  ${scrolled ? "text-gray-800" : "text-white"}
                  font-medium tracking-wide
                  transition-all duration-300
                  hover:text-[#9A134C]
                  after:absolute after:left-0 after:-bottom-1
                  after:h-[1.5px] after:w-0 after:bg-[#9A134C]
                  after:transition-all after:duration-300
                  hover:after:w-full
                `}
                style={{ fontFamily: "Playfair Display" }}
              >
                {label}
              </Link>
            ))}

            {/* DONATE BUTTON */}
            <Link
              to="/donate"
              onClick={() => window.scrollTo(0,0)}
              className="relative inline-flex rounded-xl
                bg-[#9A134C]
                shadow-[0_10px_28px_rgba(154,19,76,0.45)]
                hover:shadow-[0_14px_38px_rgba(154,19,76,0.6)]
                hover:scale-[1.03] active:scale-95
                transition-all duration-300"
            >
              <span className="relative flex items-center gap-2 px-5 py-2 rounded-xl overflow-hidden text-white font-semibold tracking-wide">
                {/* SHINE */}
                <span
                  className="absolute inset-0 pointer-events-none
                  bg-[linear-gradient(120deg,transparent,rgba(255,236,242,0.45),transparent)]
                  animate-luxuryShine"
                />
                {/* HEART ICON */}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="white"
                  className="w-5 h-5 animate-heartBeat"
                >
                  <path d="M12.1 21.35l-1.1-1.02C5.14 15.24 2 12.39 2 8.5
                    2 5.42 4.42 3 7.5 3
                    c1.74 0 3.41.81 4.5 2.09
                    C13.09 3.81 14.76 3 16.5 3
                    19.58 3 22 5.42 22 8.5
                    c0 3.89-3.14 6.74-8.9 11.83z"/>
                </svg>
                <span
                  className="relative z-10 whitespace-nowrap"
                  style={{ fontFamily: "Playfair Display", letterSpacing: "0.04em" }}
                >
                  Donate
                </span>
              </span>
            </Link>
          </div>

          {/* MOBILE RIGHT */}
          <div className="md:hidden flex items-center gap-3">
            {/* MOBILE DONATE */}
            <Link
              to="/donate"
              className="relative inline-flex rounded-xl
                bg-[#9A134C]
                shadow-[0_10px_28px_rgba(154,19,76,0.45)]
                transition-all duration-300"
            >
              <span className="relative flex items-center gap-2 px-4 py-2 rounded-xl overflow-hidden text-white font-semibold">
                <span
                  className="absolute inset-0 pointer-events-none
                  bg-[linear-gradient(120deg,transparent,rgba(255,236,242,0.45),transparent)]
                  animate-luxuryShine"
                />
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="white"
                  className="w-5 h-5 animate-heartBeat"
                >
                  <path d="M12.1 21.35l-1.1-1.02C5.14 15.24 2 12.39 2 8.5
                    2 5.42 4.42 3 7.5 3
                    c1.74 0 3.41.81 4.5 2.09
                    C13.09 3.81 14.76 3 16.5 3
                    19.58 3 22 5.42 22 8.5
                    c0 3.89-3.14 6.74-8.9 11.83z"/>
                </svg>
                <span className="relative z-10">Donate</span>
              </span>
            </Link>

          </div>
        </div>

        {/* MOBILE MENU DROPDOWN */}
        {open && (
          <div className="md:hidden mt-3 mx-4 bg-white/95 backdrop-blur-2xl shadow-[0_20px_60px_rgba(0,0,0,0.15)] rounded-2xl py-5 px-6 space-y-4 text-sm">
            {navLinks.map(([label, path]) => (
              <Link
                key={label}
                onClick={() => scrollToSection(path)}
                className="block font-medium tracking-wide text-gray-800"
                style={{ fontFamily: "Playfair Display" }}
              >
                {label}
              </Link>
            ))}
          </div>
        )}
      </nav>

      {/* MOBILE BOTTOM NAV */}
      <div className="md:hidden fixed bottom-4 left-4 right-4 z-40">
        <div
          className="flex justify-between items-center
            bg-white/85 backdrop-blur-2xl
            rounded-2xl px-5 py-3
            shadow-[0_20px_60px_rgba(0,0,0,0.18)]
            border border-white/40"
          style={{ fontFamily: "Playfair Display" }}
        >
          {navLinks.map(([label, path, Icon]) => (
            <Link
              key={label}
              onClick={() => scrollToSection(path)}
              className={`flex flex-col items-center gap-1 text-[11px] text-gray-700 transition-colors ${
                location.pathname === path ? "text-[#9A134C]" : ""
              }`}
            >
              {typeof Icon === "string" ? (
                <img src={Icon} alt={label} className="w-5 h-5 object-contain" />
              ) : (
                <Icon className="w-5 h-5 stroke-[1.6]" />
              )}
              {label}
            </Link>
          ))}
        </div>
      </div>
    </>
  );
};

export default Navbar;

